/**
 * TDG Attribution Pixel — entry point.
 *
 * Lifecycle:
 *   1. Parse config from <script data-*> + window.TDGAttributionConfig.
 *   2. Wait for consent (CMP-driven, or explicit via config/API).
 *   3. Ingest any incoming cross-domain journey from the URL.
 *   4. Detect attribution from URL/UTM/referrer and record a touchpoint.
 *   5. Inject all matching form iframes exactly once.
 *   6. Watch for late-added iframes via MutationObserver.
 *   7. Install link decorator and form submission listener.
 *   8. Expose window.TDGAttribution public API for manual control.
 */

import type { DiagnosticSnapshot, Journey, TDGAttributionPublicAPI } from './types';
import { buildConfig } from './config';
import { setDebug, log } from './utils';
import * as Consent from './consent';
import { ingestIncomingJourney, installLinkDecorator } from './crossDomain';
import { detectAttribution } from './detector';
import {
  addTouchpoint,
  getAttribution as computeAttribution,
  loadJourney,
} from './journey';
import {
  clearInjectedTracking,
  injectAllForms,
  injectBlockedForms,
  listFormState,
  resetAllForms,
  watchForLateForms,
} from './forms/injector';
import { installSubmissionListener } from './forms/submission';
import { captureUtms, loadUtms } from './utms';
import {
  beaconTouchpoint,
  getBeaconHealth,
  pushPixelLoaded,
  pushTouchpoint,
} from './analytics';
import { clearAll } from './storage';

declare const __PIXEL_VERSION__: string;
declare const __BUILD_TIME__: string;

(function bootstrap(): void {
  if (typeof window === 'undefined') return;

  // Idempotent: refuse to install twice (e.g. if tag accidentally duplicated)
  if (window.TDGAttribution) {
    console.warn('[TDGAttribution] already initialized, skipping duplicate load');
    return;
  }

  const config = buildConfig();
  setDebug(config.debug);
  log('booting v' + __PIXEL_VERSION__, config);

  let infrastructureInstalled = false;
  let pipelineRan = false;

  /** One-time global setup: link decorator, mutation observer, submission listener. */
  function installInfrastructure(): void {
    if (infrastructureInstalled) return;
    infrastructureInstalled = true;
    watchForLateForms(loadJourney, config);
    installLinkDecorator(config);
    installSubmissionListener(config);
    pushPixelLoaded(config);
  }

  /** The detection + inject pipeline. Safe to run multiple times (e.g. on
   *  consent re-grant after a revoke). Always re-reads journey from storage. */
  function runPipeline(): void {
    log(pipelineRan ? 're-running pipeline' : 'running pipeline');
    pipelineRan = true;

    ingestIncomingJourney(config);
    captureUtms();

    const source = detectAttribution(config);
    let journey: Journey = loadJourney();
    if (source) {
      journey = addTouchpoint(source, config);
      const last = journey.touchpoints[journey.touchpoints.length - 1];
      if (last && last.source === source) {
        pushTouchpoint(last, journey, config);
        beaconTouchpoint(last, config);
      }
    }

    injectAllForms(journey, config);
  }

  /**
   * Apply whatever the current consent state dictates. Idempotent —
   * always clears existing injection tracking first so the form gets
   * exactly one src rewrite per consent transition.
   */
  function applyConsentState(): void {
    const status = Consent.currentStatus();
    log('applying consent state:', status);
    clearInjectedTracking();
    if (status === 'granted') {
      runPipeline();
    } else if (status === 'denied') {
      // User has denied tracking. Wipe any previously stored journey/UTMs,
      // and mark the form submission with Osano_Blocked so Salesforce can
      // distinguish "denied consent" from "no attribution detected".
      clearAll();
      injectBlockedForms(config);
    } else {
      // Unknown — leave iframes at their bare URL until consent is decided.
      resetAllForms();
    }
  }

  Consent.initConsent(config);
  runWhenDomReady(() => {
    installInfrastructure();
    applyConsentState();
  });
  Consent.onChange(() => {
    runWhenDomReady(applyConsentState);
  });

  /* ─────────────── Public API ─────────────── */

  const api: TDGAttributionPublicAPI = {
    consent: {
      grant: Consent.grant,
      revoke: () => {
        Consent.revoke();
        clearAll();
      },
      status: Consent.currentStatus,
      onChange: Consent.onChange,
    },
    track: (source: string) => {
      const t = addTouchpoint(source, config);
      const last = t.touchpoints[t.touchpoints.length - 1];
      if (last && last.source === source) {
        pushTouchpoint(last, t, config);
        beaconTouchpoint(last, config);
      }
    },
    getJourney: () => loadJourney(),
    getAttribution: () => computeAttribution(loadJourney(), config.attributionModel),
    diagnostic: (): DiagnosticSnapshot => ({
      version: __PIXEL_VERSION__,
      buildTime: __BUILD_TIME__,
      property: config.property,
      consent: Consent.currentStatus(),
      consentProvider: Consent.activeProvider(),
      attributionModel: config.attributionModel,
      attribution: computeAttribution(loadJourney(), config.attributionModel),
      journey: loadJourney(),
      utms: loadUtms(),
      formsDetected: listFormState(),
      beaconHealth: getBeaconHealth(),
    }),
    forceUpdate: () => injectAllForms(loadJourney(), config),
    version: __PIXEL_VERSION__,
  };

  window.TDGAttribution = api;
  log('public API installed');
})();

function runWhenDomReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}
